/**
 * URLs written into a message, found again at render time.
 *
 * Same contract as mentions: the body is a plain string and stays one - nothing
 * is stored about where the links are, because the text itself is the only
 * honest record. This mirrors how Telegram treats pasted links (the visible
 * text IS the full URL, made tappable), minus the server-side preview card,
 * which would need the backend to fetch the page.
 *
 * Deliberately NOT recognised: bare domains ("vgrubs.com"). Telegram does light
 * those up, but it can - its parser ships a TLD list. Without one, every
 * "helpers.tsx" and "config.js" in a team that talks about code becomes a dead
 * link, and a chat used by this team will contain more file names than naked
 * domains. A scheme or a `www.` is the author saying "this is a link".
 */

export type LinkRun = {
  text: string;
  /** Set only on a link: where it goes. The text is what the author wrote. */
  href?: string;
};

/**
 * A scheme or `www.`, then everything up to whitespace - trimming decides how
 * much of the tail really belongs to it. Emails ride along and become mailto.
 */
const LINK_PATTERN =
  /(?:https?:\/\/|www\.)[^\s]+|[^\s@]+@[^\s@]+\.[a-z]{2,}[^\s@]*/gi;

const TRAILING_PUNCTUATION = '.,:;!?…\'"`';
const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

const countOf = (haystack: string, needle: string) =>
  haystack.split(needle).length - 1;

/**
 * How much of the match is the link, Telegram's way: punctuation against the
 * end of a sentence stays text ("pogledaj vgrubs.com/admin." must not 404),
 * and a closing bracket is kept only when the URL itself opened one - the
 * Wikipedia case, `/wiki/Foo_(bar)`, against the plain parenthesised one,
 * `(see https://vgrubs.com)`.
 */
const trimTail = (match: string) => {
  let link = match;

  for (;;) {
    const last = link[link.length - 1];

    if (TRAILING_PUNCTUATION.includes(last)) {
      link = link.slice(0, -1);
      continue;
    }

    const opener = CLOSERS[last];
    if (opener && countOf(link, last) > countOf(link, opener)) {
      link = link.slice(0, -1);
      continue;
    }

    return link;
  }
};

/** Where the written form actually points. */
const hrefFor = (link: string) => {
  if (/^https?:\/\//i.test(link)) return link;
  if (/^www\./i.test(link)) return `https://${link}`;
  return `mailto:${link}`;
};

/**
 * The text, cut into plain stretches and the links between them.
 *
 * A match that trims down to nothing but its prefix ("www." alone, a scheme
 * with no rest) is returned as the text it is.
 */
export const splitLinks = (text: string): LinkRun[] => {
  if (!text) return [];

  const runs: LinkRun[] = [];
  let cursor = 0;

  for (const match of text.matchAll(LINK_PATTERN)) {
    const link = trimTail(match[0]);

    // "www." or "http://" with nothing after it is somebody talking, not a link.
    const substantial = /^(?:https?:\/\/|www\.)?.+\..+/i.test(link);
    if (!substantial) continue;

    const start = match.index as number;
    if (start > cursor) runs.push({ text: text.slice(cursor, start) });

    runs.push({ text: link, href: hrefFor(link) });
    cursor = start + link.length;
  }

  if (cursor < text.length) runs.push({ text: text.slice(cursor) });

  return runs;
};
